<template>
  <div class="industry">
    <ftu-block
      dismiss-key="industry"
      icon="medical_services"
      title="This is the Industry view"
      description="The Industry view gives you a high level overview of the secondary market’s health with in-depth data around overall sales volume. The volume graphs show the total dollar amount spent for the given timeline (daily or monthly) and the sales count graphs show how many unique sales occurred. Verified volume describes how much of the volume was verified by the Card Ladder team."
    />

    <div class="header">
      <h1 class="secondary-font">
        Industry
      </h1>
      <p class="description">
        Please read about data limitations on this page <a href="https://cardladder.zendesk.com/hc/en-us/articles/21053788523671-What-is-the-Industry-Page" target="_blank">here</a>
      </p>
    </div>

    <div v-if="loaded">
      <div class="chart-heading">
        <div class="title-block">
          <h3 class="secondary-font">
            Monthly Volume
          </h3>

          <p class="description">
            The total amount of money spent on cards and sealed wax each month. Note this does NOT include memorabilia, tickets, etc.
          </p>
        </div>
      </div>

      <graph-overview
        graph-only
        month-labels
        :zoom.sync="monthlyVolumeZoom"
        :scale.sync="monthlyVolumeScale"
        :graph-values="[industryData.monthlyVolume.graphValues]"
      />

      <br />

      <div class="chart-heading">
        <div class="title-block">
          <h5 class="secondary-font">
            Platform Breakdown
          </h5>

          <p class="description">
            The total amount of money spent on cards and sealed wax for the 5 highest grossing platforms in the month of {{ industryData.monthlyVolume.platformMonth }}.
          </p>
        </div>
      </div>

      <div class="platform-monthly-volume">
        <div v-for="platform in industryData.monthlyVolume.platforms" :key="platform.label" class="platform">
          <div>
            {{ platform.label }}
          </div>
          <div>
            <b>{{ $filters.currency(platform.value, { full: true }) }}</b>
          </div>
        </div>
      </div>

      <br /><br />

      <div class="chart-heading">
        <div class="title-block">
          <h3 class="secondary-font">
            Daily Volume
          </h3>

          <p class="description">
            The total amount of money spent on cards tracked in Sales History each day
          </p>
        </div>
      </div>

      <graph-overview
        graph-only
        :zoom.sync="dailyVolumeZoom"
        :scale.sync="dailyVolumeScale"
        :graph-values="[industryData.dailyVolume.graphValues]"
      />

      <br /><br />

      <div class="chart-heading">
        <div class="title-block">
          <h3 class="secondary-font">
            Monthly Sales Count
          </h3>

          <p class="description">
            The total number of transactions tracked in Sales History each month
          </p>
        </div>
      </div>

      <graph-overview
        graph-only
        month-labels
        y-axis-type="number"
        :zoom.sync="monthlySalesZoom"
        :scale.sync="monthlySalesScale"
        :graph-values="[industryData.monthlySales.graphValues]"
      />

      <br /><br />

      <div class="chart-heading">
        <div class="title-block">
          <h3 class="secondary-font">
            Daily Sales Count
          </h3>

          <p class="description">
            The total number of transactions tracked in Sales History each day
          </p>
        </div>
      </div>

      <graph-overview
        graph-only
        y-axis-type="number"
        :zoom.sync="dailySalesZoom"
        :scale.sync="dailySalesScale"
        :graph-values="[industryData.dailySales.graphValues]"
      />

      <br /><br />

      <div class="chart-heading">
        <div class="title-block">
          <h3 class="secondary-font">
            Verified Monthly Volume
          </h3>

          <p class="description">
            The total amount of money spent on cards verified in the Ladder by our team each month
          </p>
        </div>
      </div>

      <graph-overview
        graph-only
        month-labels
        :zoom.sync="monthlyVerifiedVolumeZoom"
        :scale.sync="monthlyVerifiedVolumeScale"
        :graph-values="[industryData.monthlyVerifiedVolume.graphValues]"
      />

      <br /><br />

      <div class="chart-heading">
        <div class="title-block">
          <h3 class="secondary-font">
            Verified Daily Volume
          </h3>

          <p class="description">
            The total amount of money spent on cards verified in the Ladder by our team each day
          </p>
        </div>
      </div>

      <graph-overview
        graph-only
        :zoom.sync="dailyVerifiedVolumeZoom"
        :scale.sync="dailyVerifiedVolumeScale"
        :graph-values="[industryData.dailyVerifiedVolume.graphValues]"
      />
    </div>

    <spinner v-else />
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';

import { getIndustryData } from '@cardladder/shared/utils/industry.util';

import GraphOverview from '@cardladder/shared/components/GraphOverview';

import { indexService } from '@/services';

export default {
  components: {
    GraphOverview,
  },

  data() {
    return {
      industry: {},
      dashboard: {},
      loaded: false,
      dailyVolumeZoom: 92,
      dailyVolumeScale: 'linear',
      monthlyVolumeZoom: 730,
      monthlyVolumeScale: 'linear',
      monthlySalesZoom: 730,
      monthlySalesScale: 'linear',
      dailySalesZoom: 92,
      dailySalesScale: 'linear',
      monthlyVerifiedVolumeZoom: 730,
      monthlyVerifiedVolumeScale: 'linear',
      dailyVerifiedVolumeZoom: 92,
      dailyVerifiedVolumeScale: 'linear',
    };
  },

  created() {
    this.load();
  },

  computed: {
    industryData() {
      return getIndustryData(this.industry, this.dashboard);
    },
  },

  methods: {
    async load() {
      this.loaded = false;

      try {
        this.industry = await indexService.getIndustry();
        this.dashboard = await indexService.getDashboard();
      } catch (e) {
        this.$toasted.error('Error loading industry');
        Sentry.captureException(e);
      }

      this.loaded = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.industry {
  @include container;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.header {
  margin-bottom: $gp * 2;
}

h1 {
  margin-bottom: 0;
}

.chart-heading {
  margin-bottom: $gp;

  h3,
  h5 {
    margin: 0;
  }

  .title-block p.description {
    margin: $gp * .25 0 0;
  }
}

p.description {
  color: $color-gray-4;
  font-style: italic;
  font-family: $font-family-secondary;
}

.platform {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $gp * .5 0;
  font-size: 18px;
  border-bottom: 1px solid var(--theme-border);
}
</style>
